// src/pages/affiliate/MarketingMaterialsPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUI } from '../../context/UIContext';
import { useAffiliate } from '../../context/AffiliateContext';
import { isPendingApprovalError } from '../../context/AffiliateContext';
import { getBaseUrl } from '../../config/api';
import PendingApprovalView from '../../components/affiliate/PendingApprovalView';
import '../../styles/AffiliateMarketing.css';

interface MarketingMaterial {
  referralLinks: {
    primaryLink: string;
    qrCode: string;
    deepLinks: {
      ios: string;
      android: string;
    };
  };
  banners: {
    url: string;
    dimensions: string;
    type: string;
  }[];
  socialMedia: {
    platform: string;
    template: string;
    hashtags: string[];
  }[];
  emailTemplates: {
    subject: string;
    body: string;
  }[];
  instagramStoriesReels?: {
    type: string;
    dimensions: string;
    url: string;
    script: string;
  }[];
  tiktokScripts?: {
    title: string;
    duration: string;
    script: string;
    trendingAudio?: string;
  }[];
  socialMediaPosts?: {
    platform: string;
    type: string;
    dimensions: string;
    url: string;
    description: string;
  }[];
  whatsappStatus?: {
    type: string;
    dimensions?: string;
    url?: string;
    text?: string;
    description: string;
  }[];
  utmTemplates?: {
    name: string;
    link: string;
    params: Record<string, string>;
  }[];
  logoAssets?: {
    primary: string;
    icon192: string;
    icon512: string;
    svg: string;
    usage: {
      description: string;
      guidelines: string[];
      downloadNote: string;
    };
  };
}

// Banner descriptions based on type and dimensions
const getBannerDescription = (type: string, dimensions: string): string => {
  const descriptions: Record<string, string> = {
    'Medium Rectangle': 'Perfect for sidebar placements on websites and blogs. Use in content areas to promote your referral link.',
    'Leaderboard': 'Ideal for website headers and top-of-page placements. Great visibility for driving traffic.',
    'Wide Skyscraper': 'Best for sidebar placements on desktop websites. Tall format captures attention as users scroll.',
    'Half Page': 'Large vertical banner perfect for sidebar placements. High visibility format for desktop sites.',
    'Billboard': 'Large format banner for prominent website placements. Maximum visibility and impact.',
    'Rectangle': 'Perfect for sidebar placements on websites and blogs. Use in content areas to promote your referral link.',
    'Skyscraper': 'Best for sidebar placements on desktop websites. Tall format captures attention as users scroll.',
    'Mobile Banner': 'Optimized for mobile websites and apps. Perfect for in-app promotions and mobile web placements.',
    'Square': 'Versatile format for social media posts, blog sidebars, and email newsletters.'
  };

  // Return description if type matches
  if (descriptions[type]) {
    return descriptions[type];
  }

  // Fallback description based on dimensions if type not found
  if (dimensions.includes('300x250')) {
    return 'Medium rectangle banner - Perfect for website sidebars and blog placements.';
  } else if (dimensions.includes('728x90')) {
    return 'Leaderboard banner - Ideal for website headers and top placements.';
  } else if (dimensions.includes('160x600')) {
    return 'Wide skyscraper banner - Great for sidebar placements on desktop sites.';
  } else if (dimensions.includes('300x600')) {
    return 'Half page banner - Large vertical format for sidebar placements.';
  } else if (dimensions.includes('970x250')) {
    return 'Billboard banner - Large format for maximum visibility on websites.';
  } else {
    return 'Promotional banner for sharing your referral link across various platforms.';
  }
};

// Banner Card Component with proper error handling
const BannerCard: React.FC<{
  banner: { url: string; dimensions: string; type: string };
  description: string;
  onDownload: () => void;
}> = ({ banner, description, onDownload }) => {
  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState(banner.url);
  const [width, height] = banner.dimensions.split('x');
  
  // Create reliable placeholder URL
  const placeholderUrl = `https://placehold.co/${width}x${height}/667eea/ffffff?text=LookBel+${encodeURIComponent(banner.type.replace(/\s+/g, '+'))}`;
  
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!imageError) {
      setImageError(true);
      // Try fallback placeholder
      if (e.currentTarget.src !== placeholderUrl) {
        setImageUrl(placeholderUrl);
      }
    }
  };

  return (
    <div className="banner-card">
      <div className="banner-image-container">
        {!imageError ? (
          <img 
            src={imageUrl} 
            alt={`${banner.type} Banner ${banner.dimensions}`} 
            className="banner-image"
            onError={handleImageError}
            loading="lazy"
            onLoad={() => {
              // Image loaded successfully
              if (imageError) setImageError(false);
            }}
          />
        ) : (
          <div className="banner-placeholder-fallback">
            <i className="fas fa-image"></i>
            <p>{banner.type}</p>
            <span>{banner.dimensions}</span>
          </div>
        )}
      </div>
      <div className="banner-info">
        <div className="banner-meta">
          <span className="banner-dimensions">{banner.dimensions}</span>
          <span className="banner-type">{banner.type}</span>
        </div>
        <p className="banner-description">{description}</p>
      </div>
      <button
        onClick={onDownload}
        className="btn-download"
      >
        <i className="fas fa-download"></i>
        Download
      </button>
    </div>
  );
};

const AffiliateMarketingMaterialsPage: React.FC = () => {
  const { showSnackbar } = useUI();
  const navigate = useNavigate();
  const { isPending, setAffiliateStatus } = useAffiliate();
  const [materials, setMaterials] = useState<MarketingMaterial | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);

  const handleAuthError = (status: number, message?: string) => {
    if (status === 401) {
      localStorage.removeItem('affiliateToken');
      sessionStorage.removeItem('affiliateToken');
      localStorage.removeItem('affiliateUser');
      sessionStorage.removeItem('affiliateUser');
      setAffiliateStatus(null);
      showSnackbar('Session expired. Please log in again.', 'error');
      window.location.href = '/affiliate/login';
      return true;
    }
    if (status === 403 && isPendingApprovalError(status, message)) {
      setAffiliateStatus('pending');
      showSnackbar('Your account is pending approval. Marketing materials will be available once approved.', 'info');
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (isPending) return;
    fetchMarketingMaterials();
  }, [isPending]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMarketingMaterials = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
      
      if (!token) {
        showSnackbar('Please log in to view materials', 'error');
        return;
      }

      const response = await fetch(
        `${getBaseUrl()}/v1/affiliate/marketing-materials`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setMaterials(data.data);
      } else {
        console.error('Failed to load materials:', { status: response.status, data });
        if (handleAuthError(response.status, data.message)) return;
        showSnackbar(data.message || 'Failed to load materials', 'error');
      }
    } catch (error) {
      console.error('Fetch materials error:', error);
      showSnackbar('An error occurred loading materials', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      showSnackbar('Copied to clipboard!', 'success');
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      showSnackbar('Failed to copy', 'error');
    }
  };

  // Social share handlers
  const shareToFacebook = () => {
    const url = encodeURIComponent(materials?.referralLinks?.primaryLink || '');
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const shareToTwitter = () => {
    const url = encodeURIComponent(materials?.referralLinks?.primaryLink || '');
    const text = encodeURIComponent('Get amazing beauty services with LookBel! Book your appointment today 💅✨');
    const shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = () => {
    const url = encodeURIComponent(materials?.referralLinks?.primaryLink || '');
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Check out LookBel - Book Beauty Services Online!');
    const body = encodeURIComponent(`Hey!\n\nI wanted to share LookBel with you - it's an amazing platform to book beauty services from top stylists.\n\nCheck it out: ${materials?.referralLinks?.primaryLink || ''}\n\nYou won't be disappointed!`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`Check out LookBel for amazing beauty services! 💅✨\n\n${materials?.referralLinks?.primaryLink || ''}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const useWebShare = async () => {
    if (navigator.share && materials?.referralLinks?.primaryLink) {
      try {
        await navigator.share({
          title: 'LookBel - Book Beauty Services',
          text: 'Get amazing beauty services with LookBel! Book your appointment today 💅✨',
          url: materials.referralLinks.primaryLink,
        });
        showSnackbar('Shared successfully!', 'success');
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          showSnackbar('Failed to share', 'error');
        }
      }
    }
  };

  const downloadQRCode = () => {
    if (!materials?.referralLinks.qrCode) return;

    const link = document.createElement('a');
    link.href = materials.referralLinks.qrCode;
    link.download = 'referral-qr-code.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnackbar('QR Code downloaded!', 'success');
  };

  if (isPending) {
    return <PendingApprovalView title="Marketing Materials" />;
  }

  if (isLoading) {
    return (
      <div className="affiliate-marketing__loading">
        <div className="spinner"></div>
        <p>Loading marketing materials...</p>
      </div>
    );
  }

  if (!materials) {
    return (
      <div className="affiliate-marketing__error">
        <p>Failed to load marketing materials</p>
        <button onClick={fetchMarketingMaterials} className="btn-primary">Retry</button>
      </div>
    );
  }

  return (
    <div className="affiliate-marketing">
      <div className="affiliate-marketing__header">
        <div>
          <h1>Marketing Materials</h1>
          <p>Everything you need to promote LookBel</p>
        </div>
        <button onClick={() => navigate('/affiliate/dashboard')} className="btn-secondary">
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      {/* Referral Links Section */}
      <div className="affiliate-marketing__section">
        <h2>
          <i className="fas fa-link"></i>
          Referral Links
        </h2>
        
        <div className="link-card">
          <div className="link-card__header">
            <h3>Your Primary Referral Link</h3>
            <p>Share this link to track your referrals</p>
          </div>
          <div className="link-card__content">
            <input
              type="text"
              value={materials.referralLinks?.primaryLink || ''}
              readOnly
              className="link-input"
            />
            <button
              onClick={() => copyToClipboard(materials.referralLinks?.primaryLink || '', 'primary')}
              className={`btn-copy ${copied === 'primary' ? 'copied' : ''}`}
              disabled={!materials.referralLinks?.primaryLink}
            >
              <i className={`fas ${copied === 'primary' ? 'fa-check' : 'fa-copy'}`}></i>
              {copied === 'primary' ? 'Copied!' : 'Copy'}
            </button>
          </div>
          
          {/* Quick Share Buttons */}
          <div className="quick-share">
            <span className="quick-share__label">Quick Share:</span>
            <div className="quick-share__buttons">
              {typeof navigator !== 'undefined' && 'share' in navigator && (
                <button
                  onClick={useWebShare}
                  className="share-btn share-btn--native"
                  title="Share"
                  disabled={!materials.referralLinks?.primaryLink}
                >
                  <i className="fas fa-share-alt"></i>
                  <span>Share</span>
                </button>
              )}
              <button
                onClick={shareToFacebook}
                className="share-btn share-btn--facebook"
                title="Share on Facebook"
                disabled={!materials.referralLinks?.primaryLink}
              >
                <i className="fab fa-facebook-f"></i>
                <span>Facebook</span>
              </button>
              <button
                onClick={shareToTwitter}
                className="share-btn share-btn--twitter"
                title="Share on X (Twitter)"
                disabled={!materials.referralLinks?.primaryLink}
              >
                <i className="fab fa-twitter"></i>
                <span>X</span>
              </button>
              <button
                onClick={shareToLinkedIn}
                className="share-btn share-btn--linkedin"
                title="Share on LinkedIn"
                disabled={!materials.referralLinks?.primaryLink}
              >
                <i className="fab fa-linkedin-in"></i>
                <span>LinkedIn</span>
              </button>
              <button
                onClick={shareViaWhatsApp}
                className="share-btn share-btn--whatsapp"
                title="Share on WhatsApp"
                disabled={!materials.referralLinks?.primaryLink}
              >
                <i className="fab fa-whatsapp"></i>
                <span>WhatsApp</span>
              </button>
              <button
                onClick={shareViaEmail}
                className="share-btn share-btn--email"
                title="Share via Email"
                disabled={!materials.referralLinks?.primaryLink}
              >
                <i className="fas fa-envelope"></i>
                <span>Email</span>
              </button>
            </div>
          </div>
        </div>

        <div className="link-grid">
          <div className="link-card">
            <div className="link-card__header">
              <h3>QR Code</h3>
              <p>For print materials and in-person sharing</p>
            </div>
            <div className="qr-code-container">
              {materials.referralLinks.qrCode ? (
                <>
                  <img src={materials.referralLinks.qrCode} alt="Referral QR Code" className="qr-code" />
                  <button onClick={downloadQRCode} className="btn-primary">
                    <i className="fas fa-download"></i>
                    Download QR Code
                  </button>
                </>
              ) : (
                <div className="qr-code-placeholder">
                  <i className="fas fa-qrcode"></i>
                  <p>QR Code generating...</p>
                  <button onClick={fetchMarketingMaterials} className="btn-secondary">
                    Refresh
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="link-card">
            <div className="link-card__header">
              <h3>Deep Links</h3>
              <p>Direct links to open the mobile app</p>
            </div>
            <div className="deep-links">
              {materials.referralLinks.deepLinks?.ios && (
                <div className="deep-link-item">
                  <i className="fab fa-apple"></i>
                  <div className="deep-link-content">
                    <label>iOS App Link</label>
                    <code>{materials.referralLinks.deepLinks.ios}</code>
                  </div>
                  <button
                    onClick={() => copyToClipboard(materials.referralLinks.deepLinks.ios, 'ios')}
                    className="btn-icon"
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                </div>
              )}

              {materials.referralLinks.deepLinks?.android && (
                <div className="deep-link-item">
                  <i className="fab fa-android"></i>
                  <div className="deep-link-content">
                    <label>Android App Link</label>
                    <code>{materials.referralLinks.deepLinks.android}</code>
                  </div>
                  <button
                    onClick={() => copyToClipboard(materials.referralLinks.deepLinks.android, 'android')}
                    className="btn-icon"
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Banners Section */}
      <div className="affiliate-marketing__section">
        <h2>
          <i className="fas fa-images"></i>
          Banner Images
        </h2>
        <p style={{ color: '#718096', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
          Download these promotional banners to use on your website, blog, or social media. Each banner includes your unique referral code.
        </p>
        
        <div className="banner-grid">
          {materials.banners && materials.banners.length > 0 ? materials.banners.map((banner, index) => {
            const description = getBannerDescription(banner.type, banner.dimensions);
            
            const handleDownload = () => {
              // Try to download the original URL, fallback to placeholder if needed
              const downloadUrl = banner.url || `https://placehold.co/${banner.dimensions.split('x').join('x')}/667eea/ffffff?text=LookBel+${encodeURIComponent(banner.type)}`;
              const link = document.createElement('a');
              link.href = downloadUrl;
              link.download = `lookbel-banner-${banner.dimensions}-${banner.type.toLowerCase().replace(/\s+/g, '-')}.png`;
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              showSnackbar('Banner downloaded!', 'success');
            };
            
            return (
              <BannerCard
                key={index}
                banner={banner}
                description={description}
                onDownload={handleDownload}
              />
            );
          }) : (
            <div className="banner-placeholder">
              <i className="fas fa-images"></i>
              <p>Banners will be available soon</p>
            </div>
          )}
        </div>
      </div>

      {/* Social Media Templates */}
      <div className="affiliate-marketing__section">
        <h2>
          <i className="fas fa-share-alt"></i>
          Social Media Templates
        </h2>
        
        <div className="social-grid">
          {materials.socialMedia && materials.socialMedia.length > 0 ? materials.socialMedia.map((social, index) => (
            <div key={index} className="social-card">
              <div className="social-card__header">
                <i className={`fab fa-${social.platform.toLowerCase()}`}></i>
                <h3>{social.platform}</h3>
              </div>
              <div className="social-card__content">
                <p className="social-template">{social.template}</p>
                <div className="social-hashtags">
                  {social.hashtags.map((hashtag, idx) => (
                    <span key={idx} className="hashtag">#{hashtag}</span>
                  ))}
                </div>
              </div>
              <button
                onClick={() => copyToClipboard(
                  `${social.template}\n\n${social.hashtags.map(h => `#${h}`).join(' ')}\n\n${materials.referralLinks.primaryLink}`,
                  `social-${index}`
                )}
                className="btn-copy-full"
              >
                <i className={`fas ${copied === `social-${index}` ? 'fa-check' : 'fa-copy'}`}></i>
                {copied === `social-${index}` ? 'Copied!' : 'Copy Post'}
              </button>
            </div>
          )) : (
            <div className="social-placeholder">
              <i className="fas fa-share-alt"></i>
              <p>Social media templates will be available soon</p>
            </div>
          )}
        </div>
      </div>

      {/* Email Templates */}
      <div className="affiliate-marketing__section">
        <h2>
          <i className="fas fa-envelope"></i>
          Email Templates
        </h2>
        
        <div className="email-templates">
          {materials.emailTemplates && materials.emailTemplates.length > 0 ? materials.emailTemplates.map((template, index) => (
            <div key={index} className="email-card">
              <div className="email-card__header">
                <h3>{template.subject}</h3>
              </div>
              <div className="email-card__body">
                <pre>{template.body}</pre>
              </div>
              <button
                onClick={() => copyToClipboard(
                  `Subject: ${template.subject}\n\n${template.body}\n\n${materials.referralLinks.primaryLink}`,
                  `email-${index}`
                )}
                className="btn-copy-full"
              >
                <i className={`fas ${copied === `email-${index}` ? 'fa-check' : 'fa-copy'}`}></i>
                {copied === `email-${index}` ? 'Copied!' : 'Copy Template'}
              </button>
            </div>
          )) : (
            <div className="email-placeholder">
              <i className="fas fa-envelope"></i>
              <p>Email templates will be available soon</p>
            </div>
          )}
        </div>
      </div>

      {/* Instagram Stories & Reels Section */}
      {materials.instagramStoriesReels && materials.instagramStoriesReels.length > 0 && (
        <div className="affiliate-marketing__section">
          <h2>
            <i className="fab fa-instagram"></i>
            Instagram Stories & Reels
          </h2>
          <p style={{ color: '#718096', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
            Ready-to-use templates and scripts for Instagram Stories and Reels. Download images and use the scripts for your content.
          </p>
          
          <div className="instagram-grid">
            {materials.instagramStoriesReels.map((item, index) => (
              <div key={index} className="instagram-card">
                <BannerCard
                  banner={{ url: item.url, dimensions: item.dimensions, type: item.type }}
                  description={item.type}
                  onDownload={() => {
                    const link = document.createElement('a');
                    link.href = item.url;
                    link.download = `lookbel-instagram-${item.type.toLowerCase()}-${item.dimensions}.png`;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    showSnackbar('Image downloaded!', 'success');
                  }}
                />
                <div className="instagram-script">
                  <h4>{item.type} Script</h4>
                  <pre>{item.script}</pre>
                  <button
                    onClick={() => copyToClipboard(item.script, `instagram-${index}`)}
                    className="btn-copy-small"
                  >
                    <i className={`fas ${copied === `instagram-${index}` ? 'fa-check' : 'fa-copy'}`}></i>
                    {copied === `instagram-${index}` ? 'Copied!' : 'Copy Script'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TikTok Video Scripts Section */}
      {materials.tiktokScripts && materials.tiktokScripts.length > 0 && (
        <div className="affiliate-marketing__section">
          <h2>
            <i className="fab fa-tiktok"></i>
            TikTok Video Scripts
          </h2>
          <p style={{ color: '#718096', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
            Ready-to-use video scripts for TikTok. Each script includes timing, hooks, and trending audio suggestions.
          </p>
          
          <div className="tiktok-grid">
            {materials.tiktokScripts.map((script, index) => (
              <div key={index} className="tiktok-card">
                <div className="tiktok-header">
                  <h3>{script.title}</h3>
                  <span className="tiktok-duration">{script.duration}</span>
                </div>
                <div className="tiktok-script">
                  <h4>Script:</h4>
                  <pre>{script.script}</pre>
                </div>
                {script.trendingAudio && (
                  <div className="tiktok-audio">
                    <strong>Trending Audio:</strong> {script.trendingAudio}
                  </div>
                )}
                <button
                  onClick={() => copyToClipboard(script.script, `tiktok-${index}`)}
                  className="btn-copy-full"
                >
                  <i className={`fas ${copied === `tiktok-${index}` ? 'fa-check' : 'fa-copy'}`}></i>
                  {copied === `tiktok-${index}` ? 'Copied!' : 'Copy Script'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Social Media Post Images Section */}
      {materials.socialMediaPosts && materials.socialMediaPosts.length > 0 && (
        <div className="affiliate-marketing__section">
          <h2>
            <i className="fas fa-share-alt"></i>
            Social Media Post Images
          </h2>
          <p style={{ color: '#718096', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
            Platform-specific images optimized for each social media platform. Download and customize for your posts.
          </p>
          
          <div className="social-posts-grid">
            {materials.socialMediaPosts.map((post, index) => (
              <div key={index} className="social-post-card">
                <BannerCard
                  banner={{ url: post.url, dimensions: post.dimensions, type: post.type }}
                  description={post.description}
                  onDownload={() => {
                    const link = document.createElement('a');
                    link.href = post.url;
                    link.download = `lookbel-${post.platform.toLowerCase()}-${post.type.toLowerCase().replace(/\s+/g, '-')}-${post.dimensions}.png`;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    showSnackbar('Image downloaded!', 'success');
                  }}
                />
                <div className="social-post-info">
                  <div className="social-post-platform">
                    <i className={`fab fa-${post.platform.toLowerCase() === 'twitter/x' ? 'twitter' : post.platform.toLowerCase()}`}></i>
                    <span>{post.platform}</span>
                  </div>
                  <div className="social-post-type">{post.type}</div>
                  <div className="social-post-dimensions">{post.dimensions}</div>
                  <p className="social-post-description">{post.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* WhatsApp Status Templates Section */}
      {materials.whatsappStatus && materials.whatsappStatus.length > 0 && (
        <div className="affiliate-marketing__section">
          <h2>
            <i className="fab fa-whatsapp"></i>
            WhatsApp Status Templates
          </h2>
          <p style={{ color: '#718096', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
            Templates for WhatsApp status updates. Use images or text to share your referral link.
          </p>
          
          <div className="whatsapp-grid">
            {materials.whatsappStatus.map((status, index) => (
              <div key={index} className="whatsapp-card">
                <div className="whatsapp-header">
                  <i className="fab fa-whatsapp"></i>
                  <h3>{status.type}</h3>
                </div>
                {status.url && (
                  <BannerCard
                    banner={{ url: status.url, dimensions: status.dimensions || '1080x1920', type: status.type }}
                    description={status.description}
                    onDownload={() => {
                      const link = document.createElement('a');
                      link.href = status.url!;
                      link.download = `lookbel-whatsapp-status-${index}.png`;
                      link.target = '_blank';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      showSnackbar('Image downloaded!', 'success');
                    }}
                  />
                )}
                {status.text && (
                  <div className="whatsapp-text">
                    <pre>{status.text}</pre>
                    <button
                      onClick={() => copyToClipboard(status.text!, `whatsapp-${index}`)}
                      className="btn-copy-full"
                    >
                      <i className={`fas ${copied === `whatsapp-${index}` ? 'fa-check' : 'fa-copy'}`}></i>
                      {copied === `whatsapp-${index}` ? 'Copied!' : 'Copy Text'}
                    </button>
                  </div>
                )}
                <p className="whatsapp-description">{status.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* UTM Parameter Generator Section */}
      {materials.utmTemplates && materials.utmTemplates.length > 0 && (
        <div className="affiliate-marketing__section">
          <h2>
            <i className="fas fa-link"></i>
            UTM Parameter Generator
          </h2>
          <p style={{ color: '#718096', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
            Pre-configured tracking links with UTM parameters for different campaigns. Track which platforms drive the most conversions!
          </p>
          
          <div className="utm-grid">
            {materials.utmTemplates.map((template, index) => (
              <div key={index} className="utm-card">
                <div className="utm-header">
                  <h3>{template.name}</h3>
                </div>
                <div className="utm-link">
                  <input
                    type="text"
                    value={template.link}
                    readOnly
                    className="utm-input"
                  />
                  <button
                    onClick={() => copyToClipboard(template.link, `utm-${index}`)}
                    className="btn-copy-small"
                  >
                    <i className={`fas ${copied === `utm-${index}` ? 'fa-check' : 'fa-copy'}`}></i>
                    {copied === `utm-${index}` ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="utm-params">
                  <strong>UTM Parameters:</strong>
                  <ul>
                    {Object.entries(template.params).map(([key, value]) => (
                      <li key={key}>
                        <code>{key}</code>: <span>{value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logo Assets Section */}
      {materials?.logoAssets && (
        <div className="affiliate-marketing__section">
          <h2>
            <i className="fas fa-image"></i> LookBel Logo Assets
          </h2>
          <p className="affiliate-marketing__section-description">
            Download LookBel logos to use in your marketing materials and maintain brand consistency.
          </p>
          
          <div className="logo-assets-grid">
            <div className="logo-asset-card">
              <div className="logo-preview">
                <img src={materials.logoAssets.primary} alt="LookBel Logo" />
              </div>
              <div className="logo-asset-info">
                <h3>Primary Logo (512px)</h3>
                <p>Best for banners, large displays, and print materials</p>
                <a 
                  href={materials.logoAssets.primary} 
                  download="lookbel-logo-512.png"
                  className="btn-download-logo"
                >
                  <i className="fas fa-download"></i> Download
                </a>
              </div>
            </div>

            <div className="logo-asset-card">
              <div className="logo-preview">
                <img src={materials.logoAssets.icon192} alt="LookBel Icon" />
              </div>
              <div className="logo-asset-info">
                <h3>Icon (192px)</h3>
                <p>Perfect for social media profile pictures and small spaces</p>
                <a 
                  href={materials.logoAssets.icon192} 
                  download="lookbel-logo-192.png"
                  className="btn-download-logo"
                >
                  <i className="fas fa-download"></i> Download
                </a>
              </div>
            </div>

            <div className="logo-asset-card">
              <div className="logo-preview">
                <img 
                  src={materials.logoAssets.svg} 
                  alt="LookBel SVG" 
                  style={{ maxWidth: '100px', height: 'auto' }}
                  onError={(e) => {
                    // Fallback to primary logo if SVG fails to load
                    if (materials.logoAssets?.primary) {
                      e.currentTarget.src = materials.logoAssets.primary;
                    }
                  }}
                />
              </div>
              <div className="logo-asset-info">
                <h3>SVG Logo</h3>
                <p>Vector format - scalable for any size without quality loss</p>
                <a 
                  href={materials.logoAssets.svg} 
                  download="lookbel-logo.svg"
                  className="btn-download-logo"
                >
                  <i className="fas fa-download"></i> Download
                </a>
              </div>
            </div>
          </div>

          <div className="logo-guidelines">
            <h3>
              <i className="fas fa-info-circle"></i> Logo Usage Guidelines
            </h3>
            <p>{materials.logoAssets.usage.description}</p>
            <ul>
              {materials.logoAssets.usage.guidelines.map((guideline, index) => (
                <li key={index}>{guideline}</li>
              ))}
            </ul>
            <p className="download-note">
              <i className="fas fa-download"></i> {materials.logoAssets.usage.downloadNote}
            </p>
          </div>
        </div>
      )}

      {/* Tips Section */}
      <div className="affiliate-marketing__section">
        <h2>
          <i className="fas fa-lightbulb"></i>
          Promotion Tips
        </h2>
        
        <div className="tips-grid">
          <div className="tip-card">
            <i className="fas fa-users"></i>
            <h3>Know Your Audience</h3>
            <p>Share content that resonates with your followers' interests in beauty and wellness.</p>
          </div>

          <div className="tip-card">
            <i className="fas fa-calendar-alt"></i>
            <h3>Post Consistently</h3>
            <p>Regular posting keeps your audience engaged and increases conversion chances.</p>
          </div>

          <div className="tip-card">
            <i className="fas fa-camera"></i>
            <h3>Use Visual Content</h3>
            <p>Before/after photos and video testimonials perform best on social media.</p>
          </div>

          <div className="tip-card">
            <i className="fas fa-comments"></i>
            <h3>Engage with Comments</h3>
            <p>Respond to questions and comments to build trust with your audience.</p>
          </div>

          <div className="tip-card">
            <i className="fas fa-chart-line"></i>
            <h3>Track Performance</h3>
            <p>Monitor your dashboard to see which platforms drive the most conversions.</p>
          </div>

          <div className="tip-card">
            <i className="fas fa-star"></i>
            <h3>Share Your Story</h3>
            <p>Personal experiences with LookBel services are more convincing than generic ads.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateMarketingMaterialsPage;

